/**
 * Manager role-assumption — sessionStorage-backed token store.
 *
 * Holds the active assumption token (minted by server-node's
 * /startRoleAssumption handler) plus metadata about the assumed
 * organization and role. Decoupled from React: any module (e.g.
 * authUtils.authHeadersFor) can read it without going through hooks.
 *
 * Storage key: sessionStorage 'ct_role_assumption'. sessionStorage (not
 * local) is intentional — closing the tab ends the assumption session.
 * (As a consequence, sessions are per-tab; the `storage` event listener
 * below is defensive only — it fires for cross-tab localStorage writes
 * and rarely for sessionStorage in same-origin tabs.)
 *
 * Subscribers (React components via useSyncExternalStore) are notified
 * via a window event 'ct-role-assumption-change' dispatched on any
 * write.
 *
 * Tokens are validated against `expiresAt` (ISO string) on read. Expired
 * entries are reported as null by `getRoleAssumption` but the underlying
 * sessionStorage entry is NOT cleared by the read path itself — clearing is
 * owned solely by `useRoleAssumption`'s expiry effect so that the React
 * hook is the single source of truth for expiry-driven cleanup + reload.
 * See `getRoleAssumption` below for the full rationale.
 * Hasura/server-node enforce the JWT `exp` claim server-side regardless;
 * this is just for UX.
 *
 * Snapshot caching: `useSyncExternalStore` requires getSnapshot() to
 * return a stable reference between renders when the underlying state
 * has not changed. JSON.parse() always returns a fresh object, so we
 * keep a module-level cache keyed by the raw sessionStorage string and
 * only re-parse when that string changes. The cache is invalidated on
 * every write.
 */

const STORAGE_KEY = "ct_role_assumption"
const CHANGE_EVENT = "ct-role-assumption-change"

// Snapshot cache — see header doc. `cachedRaw === null` means "no entry
// in storage" (distinct from "cache cold"); we use the sentinel below
// to represent the cold state.
const CACHE_COLD = Symbol("cache-cold")
let cachedRaw = CACHE_COLD
let cachedEntry = null

const invalidateCache = () => {
  cachedRaw = CACHE_COLD
  cachedEntry = null
}

const safeConsoleWarn = (...args) => {
  try {
    // eslint-disable-next-line no-console
    console.warn(...args)
  } catch {
    // ignore
  }
}

const isPlausibleEntry = (entry) => {
  if (!entry || typeof entry !== "object") return false
  if (typeof entry.token !== "string" || entry.token.length === 0) return false
  if (typeof entry.expiresAt !== "string" || entry.expiresAt.length === 0) return false
  if (!entry.assumed || typeof entry.assumed !== "object") return false
  if (typeof entry.assumed.role !== "string" || typeof entry.assumed.orgId !== "string") return false
  return true
}

const isExpired = (entry) => {
  const exp = Date.parse(entry?.expiresAt ?? "")
  if (Number.isNaN(exp)) return true
  return Date.now() >= exp
}

const readRaw = () => {
  let raw
  try {
    raw = sessionStorage.getItem(STORAGE_KEY)
  } catch (err) {
    safeConsoleWarn("[roleAssumptionStore] read failed:", err?.message ?? err)
    return null
  }

  if (!raw) {
    if (cachedRaw !== null) {
      cachedRaw = null
      cachedEntry = null
    }
    return null
  }

  // Cache hit — same serialized bytes as last read, return the cached
  // parsed object so useSyncExternalStore sees a stable reference.
  if (raw === cachedRaw) return cachedEntry

  // Cache miss — parse, validate, populate.
  let parsed
  try {
    parsed = JSON.parse(raw)
  } catch (err) {
    safeConsoleWarn("[roleAssumptionStore] parse failed:", err?.message ?? err)
    cachedRaw = raw
    cachedEntry = null
    return null
  }
  if (!isPlausibleEntry(parsed)) {
    safeConsoleWarn("[roleAssumptionStore] discarding malformed sessionStorage entry")
    cachedRaw = raw
    cachedEntry = null
    return null
  }
  cachedRaw = raw
  cachedEntry = parsed
  return parsed
}

const writeRaw = (entry) => {
  try {
    if (entry) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    // Invalidate so the next read repopulates from current storage state.
    invalidateCache()
    return true
  } catch (err) {
    // sessionStorage may be unavailable (private mode, quota exceeded, etc.)
    safeConsoleWarn("[roleAssumptionStore] write failed:", err?.message ?? err)
    return false
  }
}

const notify = () => {
  try {
    window.dispatchEvent(new Event(CHANGE_EVENT))
  } catch {
    // noop — environments without window/Event won't load this module anyway
  }
}

/**
 * Returns the active assumption entry, or null if absent / expired /
 * malformed. **Pure read — does not mutate sessionStorage**, even when
 * the entry is expired.
 *
 * Why pure: there are many callers on every render and every fetch
 * (role helpers in authUtils, the header's canAssumeRole computation,
 * etc.). If any of them happen to hit this function FIRST after
 * expiry, they would clear storage and notify subscribers — at which
 * point the React hook's snapshot becomes null, the hook never sees
 * `entry && remainingMs === 0`, and the reload effect never fires. To
 * make the reload deterministic, `useRoleAssumption` is the **single
 * owner** of expiry-driven clearing + reload.
 *
 * Callers that need an expired-aware token (authHeadersFor) get null
 * back here and fall back to the real Auth0 token. Storage stays
 * populated until the hook clears it, which is benign — the entry
 * is treated as expired by every reader.
 */
export const getRoleAssumption = () => {
  const entry = readRaw()
  if (!entry) return null
  if (isExpired(entry)) return null
  return entry
}

/**
 * Returns the raw assumption entry from storage, INCLUDING when expired
 * (so `useRoleAssumption` can observe the expired entry and run its
 * clear-and-reload effect). Malformed/missing entries surface as `null`
 * — `readRaw` filters them out via `isPlausibleEntry` and never exposes
 * partially-parsed garbage. Designed for `useSyncExternalStore.getSnapshot`.
 *
 * Snapshot stability: returns the same cached object reference between
 * calls when sessionStorage is unchanged (see `readRaw` cache logic),
 * as required by useSyncExternalStore's contract.
 */
export const peekRoleAssumption = () => {
  return readRaw()
}

/**
 * Returns the assumption JWT if active (and not expired), else null.
 * Used by authHeadersFor to override the Authorization header at the
 * fetch layer. Built on `getRoleAssumption` so expired tokens are
 * not handed out — fetches fall back to the real Auth0 token.
 */
export const getRoleAssumptionToken = () => {
  return getRoleAssumption()?.token ?? null
}

/**
 * Persist a new assumption session. `entry` shape:
 *   { token, expiresAt, ttlMinutes, assumed: { orgId, orgName, role } }
 *
 * Throws if the entry is malformed or if sessionStorage write fails — the
 * caller (modal) shows the error to the user instead of silently dropping it.
 */
export const setRoleAssumption = (entry) => {
  if (!isPlausibleEntry(entry)) {
    throw new Error("Invalid assumption entry: missing token, expiresAt, or assumed fields")
  }
  if (isExpired(entry)) {
    throw new Error("Refusing to persist an already-expired assumption token")
  }
  const ok = writeRaw(entry)
  if (!ok) {
    throw new Error("Unable to persist assumption — browser storage is not available")
  }
  notify()
}

/** Remove any active assumption entry. Safe to call when none exists. */
export const clearRoleAssumption = () => {
  writeRaw(null)
  notify()
}

/**
 * Subscribe to assumption state changes. Used by useSyncExternalStore.
 * Returns an unsubscribe function. Listens to both same-tab custom events
 * and cross-tab 'storage' events.
 */
export const subscribeRoleAssumption = (listener) => {
  const onCustom = () => listener()
  const onStorage = (e) => {
    if (!e || e.key === STORAGE_KEY) listener()
  }
  window.addEventListener(CHANGE_EVENT, onCustom)
  window.addEventListener("storage", onStorage)
  return () => {
    window.removeEventListener(CHANGE_EVENT, onCustom)
    window.removeEventListener("storage", onStorage)
  }
}
